import Swal from 'sweetalert2';

/**
 * Centralized SweetAlert2 wrappers.
 *
 * IMPORTANT: every confirm helper returns the result so callers can do:
 *   const result = await confirmDelete(...);
 *   if (result.isConfirmed) { ... }
 *
 * (Per spec: check swalResult.isConfirmed, NOT Swal.isConfirmed.)
 */

// shared visual config — picks up our CSS overrides from global.css
const SHARED = {
  buttonsStyling: true,
  reverseButtons: true,
  background: undefined, // let CSS vars handle it
};

export function toast(message, icon = 'success') {
  return Swal.fire({
    ...SHARED,
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    icon,
    title: message,
  });
}

export function notifySuccess(title, text) {
  return Swal.fire({
    ...SHARED,
    icon: 'success',
    title,
    text,
    confirmButtonText: 'Got it',
  });
}

export function notifyError(title, text) {
  return Swal.fire({
    ...SHARED,
    icon: 'error',
    title: title || 'Something went wrong',
    text: text || 'Please try again.',
    confirmButtonText: 'Close',
  });
}

export function notifyInfo(title, text) {
  return Swal.fire({
    ...SHARED,
    icon: 'info',
    title,
    text,
    confirmButtonText: 'OK',
  });
}

/**
 * Yes/No confirmation. Returns the SweetAlert result object;
 * caller checks `.isConfirmed`.
 */
export function confirm({
  title,
  text,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  icon = 'question',
}) {
  return Swal.fire({
    ...SHARED,
    icon,
    title,
    text,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
  });
}

/**
 * Three-button completion dialog used on the Operational page:
 *   - Push & Email   → resolves { isConfirmed: true,  isDenied: false }
 *   - Normal Push    → resolves { isConfirmed: false, isDenied: true  }
 *   - Cancel         → resolves { isDismissed: true }
 *
 * Caller should branch on isConfirmed / isDenied / isDismissed.
 */
export function confirmCompletion(name) {
  return Swal.fire({
    ...SHARED,
    title: 'Mark as completed?',
    html: name
      ? `Push <strong>${escapeHtml(name)}</strong>'s appointment to <em>completed</em>.<br/><span style="color:var(--plum-soft);font-size:13px;">Pick how to wrap it up.</span>`
      : 'Pick how to wrap up this appointment.',
    icon: 'question',
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Push & Email',
    denyButtonText: 'Normal Push',
    cancelButtonText: 'Cancel',
    focusConfirm: true,
  });
}

/**
 * Soft-delete confirmation, with a slightly stronger visual.
 */
export function confirmDelete(label = 'this item') {
  return Swal.fire({
    ...SHARED,
    title: 'Move to trash?',
    text: `${label} will be moved to Trash. You can restore it later.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Move to trash',
    cancelButtonText: 'Cancel',
  });
}

export function confirmRestore(label = 'this item') {
  return Swal.fire({
    ...SHARED,
    title: 'Restore?',
    text: `${label} will be restored to its previous location.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Restore',
    cancelButtonText: 'Cancel',
  });
}

/**
 * Loading splash for long-running ops.
 */
export function showLoading(title = 'Working…') {
  Swal.fire({
    ...SHARED,
    title,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading(),
  });
}

export function closeLoading() {
  Swal.close();
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const swalHelpers = {
  toast,
  notifySuccess,
  notifyError,
  notifyInfo,
  confirm,
  confirmCompletion,
  confirmDelete,
  confirmRestore,
  showLoading,
  closeLoading,
};
export default swalHelpers;